/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { makeGet } from "@culturehq/client";
import { classnames, Icon, PlainButton, Thumbnail } from "@culturehq/components";

import { useGaTracking, useInsideIframe, useOrganizationTitle } from "app/hooks";
import { getImageFromStory, getUserName, isIntellia, isMerck, isMSD, isTarget, Trans } from "app/utils";
import displayLastName from "app/utils/displayLastName";
import imagesSizes from "constants/imagesSizes";

import Subtitles from "components/common/Subtitles";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import getText from "components/editor/getText";
import LightboxImageIframe from "components/common/gallery/LightboxImageIframe";

import BodyTags from "components/common/BodyTags";

import TargetPlayButtonIcon from "components/common/icons/TargetPlayButtonIcon";
import StoryQuestionTitle from "components/stories/StoryQuestionTitle";

const TABLET_BREAKPOINT = 992;

const UserInfo = ({ story, insideIframe }) => (
  <div className="Story__creator">
    <div className="Story__creator-container">
      <Thumbnail image={story.creator.avatar.thumbUrl} />
      <div>
        <p className="Story__creator-text">{getUserName(story.creator, displayLastName(story.organization.name))}</p>
        <p className="Story__creator-text Story__creator-title">{story.creator.title}</p>
      </div>
    </div>
    {insideIframe && !isTarget(story.organization.name) && (
      <a
        className="Story__link"
        href={`/story/${story.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={event => {
          event.stopPropagation();
        }}
      >
        <Icon className="link-icon" icon="android-open" />
      </a>
    )}
  </div>
);

const EmbedStoryCallout = ({ match, handleImageLoad, orgName }) => {
  const mediaRef = useRef();
  const answerRef = useRef();
  const handleRef = useRef();
  const { gaSessionId, gaClientId, trackEvent } = useGaTracking();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [story, setStory] = useState();
  const [showVideoInfo, setShowVideoInfo] = useState(true);
  const [showShowMoreButton, setShowShowMoreButton] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [body, setBody] = useState();
  const [handle, setHandle] = useState();
  const insideIframe = useInsideIframe();
  const { updateSeoTitle } = useOrganizationTitle();
  const [isLargeDevice, setIsLargeDevice] = useState(window.innerWidth > TABLET_BREAKPOINT);

  handleRef.current = handle;

  const isMSDorMerck = isMSD(story?.organization?.name) || isMerck(story?.organization?.name);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeDevice(window.innerWidth > TABLET_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(
    () => {
      const trackData = (eventAction, loadedStory) => trackEvent({
        organizationId: loadedStory.organization.id,
        storyId: loadedStory.id,
        eventAction,
        url: window.location.href,
        type: insideIframe ? "story_iframe" : "story_page"
      });

      const getStory = async id => {
        try {
          const { story } = await makeGet(`/embed/stories/${id}?web=true`);
          setStory(story);
          updateSeoTitle(story.organization.name, story.question.question);
          setBody(getText(story.body));

          if (gaSessionId && gaClientId && ((!isIntellia(orgName)) || !insideIframe)) {
            trackData("loading_story", story);
          }
        } catch {
        // an error happened
        }
      };

      if (match.params.storyId) {
        getStory(match.params.storyId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [match.params.storyId, insideIframe, gaClientId, gaSessionId]
  );

  useLayoutEffect(() => {
    const checkShowMore = () => {
      if (answerRef.current?.offsetWidth < answerRef.current?.scrollWidth) {
        setShowShowMoreButton(true);
      } else {
        setShowShowMoreButton(false);
      }
    };
    window.addEventListener("resize", checkShowMore);
    return () => window.removeEventListener("resize", checkShowMore);
  }, [answerRef]);

  useEffect(
    () => {
      if (answerRef.current && answerRef.current.offsetWidth < answerRef.current.scrollWidth) {
        setShowShowMoreButton(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answerRef.current]
  );

  const trackData = (eventAction, storyId = undefined, params = {}) => {
    if ((!isIntellia(orgName)) || !insideIframe) {
      trackEvent({
        organizationId: story.organization.id,
        storyId,
        eventAction,
        url: window.location.href,
        type: insideIframe ? "story_iframe" : "story_page",
        ...params
      });
    }
  };

  const handlePlay = () => {
    if (story.media.mediaType === "video") mediaRef.current.play();
  };

  const handleSeeMore = event => {
    event.stopPropagation();
    setShowMore(!showMore);
  };

  const handleMouseOver = () => {
    handleVideoStoryPause();
    setHandle(setTimeout(() => {
      trackData("over_story");
    }, 3000));
  };

  const handleMouseLeave = () => {
    if (handleRef.current) {
      clearTimeout(handleRef.current);
      setHandle(undefined);
    }
  };

  const handleVideoEnded = () => {
    setShowVideoInfo(true);
  };

  const handleVideoStoryPlay = () => {
    setIsVideoPlaying(true);
    setShowVideoInfo(false);
    setVideoStartTime(Date.now());
    trackData("play_video_story", story.id);
  };

  const handleVideoStoryPause = () => {
    if (isVideoPlaying && videoStartTime) {
      const duration = (Date.now() - videoStartTime) / 1000;
      trackData("play_duration_story_video", story.id, { time: duration });
      setIsVideoPlaying(false);
      setVideoStartTime(null);
    }
  };

  if (!story) {
    return <></>;
  }

  if (isLargeDevice && !isMSDorMerck) {
    return (
      <div
        className={classnames("EmbedStory", "gallery-lightbox--Story", story.organization.name.replace(/\s+/g, "-").toLowerCase(), { "EmbedStory--Iframe": insideIframe }, "row")}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <div className="gallery-lightbox__photo col-sm-7 col-xs-12">
          <div className="EmbedStory__Container" style={{ backgroundImage: `url(${story.media.mediaType === "video" ? story.media.thumbnail : getImageFromStory(imagesSizes.NORMAL, story)})` }}>
            <div className="EmbedStory__Container-Overlay" />
            {story.media.mediaType === "image" && (
              <LightboxImageIframe
                boxImages={story.visuals}
                onImageLoad={handleImageLoad}
              />
            )}
            {story.media.mediaType === "video" && (
              <video
                ref={mediaRef}
                controls={isTarget(story.organization.name) || !showVideoInfo}
                onPause={handleVideoStoryPause}
                onPlay={handleVideoStoryPlay}
                onEnded={handleVideoEnded}
                poster={`${story.media.thumbnail}?not-from-cache-please`}
                src={story.media.url}
                disablePictureInPicture
                crossOrigin="anonymous"
                controlsList="nodownload"
                className="media"
              >
                <Subtitles media={story.media} />
              </video>
            )}
            {showVideoInfo && (
              <>
                {story.media.mediaType === "video" && (
                  <div className={classnames("background-container", { "background-container-shadow": showMore })} onClick={handlePlay} role="button" tabIndex={0} />
                )}
                {!showMore && story.media.mediaType === "video" && !isTarget(story.organization.name) && (
                  <Icon onClick={handlePlay} className="play-icon" icon="play-no-border" />
                )}
                {!showMore && !isMSDorMerck && (
                  <div className="Story__response" onClick={handlePlay} role="button" tabIndex={0} />
                )}
              </>
            )}
          </div>
        </div>
        <div className="gallery-lightbox__details-container col-sm-5 col-xs-12">
          <div className="scrollable-container no-scrollbar">
            <StoryQuestionTitle
              storyQuestion={story.parentQuestion?.question || story.question}
              externalView
              orgName={story.organization.name}
            />
            <hr />
            <div className="gallery-lightbox__creator-container">
              <Thumbnail className="Thumbnail" image={story.creator.avatar.thumbUrl} title={getUserName(story.creator, displayLastName(story.organization.name))} />
              <div className="gallery-lightbox__creator-information">
                <span className="gallery-lightbox__uploader-creator">{getUserName(story.creator, displayLastName(story.organization.name))}</span>
                <div className="gallery-lightbox__uploader-date">{story.creator.title}</div>
              </div>
            </div>
            <LazyEditorOutput output={body} showTags={false} />
            <BodyTags
              tags={story.bodyTags}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames("EmbedStory", story.organization.name.replace(/\s+/g, "-").toLowerCase(), { "EmbedStory--Iframe": insideIframe })}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <div className="EmbedStory__Container" style={{ backgroundImage: `url(${story.media.mediaType === "video" ? story.media.thumbnail : getImageFromStory(imagesSizes.NORMAL, story)})` }}>
        <div className="EmbedStory__Container-Overlay" />
        {story.media.mediaType === "image" && (
          <LightboxImageIframe
            className="media"
            boxImages={story.visuals}
            onImageLoad={handleImageLoad}
          />
        )}
        {story.media.mediaType === "video" && (
          <>
            <video
              ref={mediaRef}
              controls={!showVideoInfo}
              className="media"
              onPause={handleVideoStoryPause}
              onPlay={handleVideoStoryPlay}
              onEnded={handleVideoEnded}
              poster={`${story.media.thumbnail}?not-from-cache-please`}
              src={story.media.url}
              disablePictureInPicture
              crossOrigin="anonymous"
              controlsList="nodownload"
            >
              <Subtitles media={story.media} />
            </video>
          </>
        )}
        {showVideoInfo && (
          <>
            {story.media.mediaType === "video" ? (
              <div className={classnames("background-container", { "background-container-shadow": showMore })} onClick={handlePlay} role="button" tabIndex={0}>
                {!isMSDorMerck && (
                  <UserInfo insideIframe={insideIframe} story={story} />
                )}
                {showMore && (
                  <div className="Story__Info">
                    <h1 className="Story__question">{story.parentQuestion?.question || story.question.question}</h1>
                    <LazyEditorOutput
                      output={story.body}
                      showTags={false}
                    />
                    <PlainButton className="Story__more" onClick={handleSeeMore}>see less</PlainButton>
                  </div>
                )}
              </div>
            ) : (
              <div className={classnames("background-container", { "background-container-shadow": showMore })}>
                {!isMSDorMerck && (
                  <UserInfo insideIframe={insideIframe} story={story} />
                )}
                {showMore && (
                  <div className="Story__Info">
                    <h1 className="Story__question">{story.parentQuestion?.question || story.question.question}</h1>
                    <LazyEditorOutput
                      output={story.body}
                      showTags={false}
                    />
                    <PlainButton className="Story__more" onClick={handleSeeMore}>see less</PlainButton>
                  </div>
                )}
              </div>
            )}
            {!showMore && story.media.mediaType === "video" && !isTarget(story.organization.name) && (
              <Icon onClick={handlePlay} className="play-icon" icon="play-no-border" />
            )}
            {!showMore && !isMSDorMerck && (
              <div className="Story__response" onClick={handlePlay} role="button" tabIndex={0}>
                {isTarget(story.organization.name) ? (
                  <p className="Story__question">{story.parentQuestion?.question || story.question.question}</p>
                ) : (
                  <h1 className="Story__question">{story.parentQuestion?.question || story.question.question}</h1>
                )}
                {!isTarget(story.organization.name) && (
                  <p className="Story__answer" ref={answerRef}>
                    {body}
                  </p>
                )}
                {showShowMoreButton && <PlainButton className="Story__more" onClick={handleSeeMore}>see more</PlainButton>}
                {isTarget(story.organization.name) && (
                  <div className="play-container" onClick={handlePlay}>
                    <span className="play-text">
                      <Trans>Play</Trans>
                    </span>
                    <TargetPlayButtonIcon className="TargetPlayButtonIcon" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmbedStoryCallout;
